import jwtDecode from "jwt-decode";
import axios from "src/utils/axios";
import {
  signinInterface,
  regis_phone_response,
  regisWithPhoneNumberProps,
} from "auth-types";
import { REST_URI } from "src/config";
import { eraseCookie } from "src/utils/utils";
import {
  changeNameUriType,
  addOpenAccountType,
  bitkubRegisterationInputType,
  bitkubRegisterationType,
  settradeRegisterationInputType,
  settradeRegisterationType,
  sttAuthenResponseType,
  sttRegisInputType,
  sttRegisRequestResponseType,
  sttRegisResponseType,
  sttUriType,
  switchBrokerType,
  loginType,
  requestRegisPhoneNoType,
  requestResetPasswordType,
} from "./authInterface";
import { errorType } from "src/types/error-types";
import { clientError } from "src/context/errors";
import { ERROR_CODE } from "src/context/constants/errorCode";

const makeError = (code: string, message: string) => {
  const error: errorType = {
    __typename: "Error",
    code: code,
    message: message,
  };

  return error;
};

const wrapError = (data: any) => {
  return { ...data, __typename: "Error" } as errorType;
};

class AuthService {
  logout = async () => {
    this.setSession(null);
    eraseCookie("accessToken");
    try {
      await axios.post(`${REST_URI}/logout`);
      // window.location.reload();
    } catch (err) {}
  };

  requestResetPassword = async (brokerID: string, phoneNo: string) => {
    if (!brokerID) return clientError.missingBrokerID;
    if (!phoneNo) return clientError.missingPhoneNo;
    if (phoneNo[0] !== "0") return clientError.phoneNoMustStartWith0;
    if (phoneNo.length !== 10) return clientError.invalidPhoneNo;

    try {
      const res = await axios.post(`${REST_URI}/resetPassword/request`, {
        phoneNo: phoneNo,
        brokerID: brokerID,
      });
      if (res.status !== 200) return clientError.requestResetPasswordFail;
      return {
        __typename: "RequestResetPassword",
        message: res.data.message,
        success: res.data.success,
      } as requestResetPasswordType;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData?.code === ERROR_CODE.OTP_BAN) return wrapError(errData);
      if (err?.response?.status === 429) return clientError.proxyBlock;
      if (err?.response?.status === 500) return clientError.somethingWentWrong;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.proxyBlock;
    }
  };

  resetPassword = (password: string, brokerID: string) =>
    new Promise(async (resolve, reject) => {
      let body = {
        newPassword: password,
        brokerID: brokerID,
      };
      try {
        await axios.post(`${REST_URI}/resetPassword`, body);
        resolve(null);
      } catch (err: any) {
        if (err?.response?.data?.message) reject(err.response.data.message);
        reject("Something went wrong :(");
      }
    });

  //registeRequest ... (send phoneNo,brokerID then get otpSession)
  async requestRegisPhoneNo(brokerID: string, phoneNo: string) {
    if (!brokerID) return clientError.missingBrokerID;
    if (!phoneNo) return clientError.missingPhoneNo;
    if (phoneNo[0] !== "0") return clientError.phoneNoMustStartWith0;
    if (phoneNo.length !== 10) return clientError.invalidPhoneNo;

    try {
      const res = await axios.post(`${REST_URI}/register/request`, {
        phoneNo: phoneNo,
        brokerID: brokerID,
      });
      if (res.status !== 200) return clientError.requestRegisPhoneNoFail;
      return {
        __typename: "RequestRegisPhoneNo",
        message: res.data.message,
        success: res.data.success,
      } as requestRegisPhoneNoType;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData?.code === ERROR_CODE.OTP_BAN) return wrapError(errData);
      if (err?.response?.status === 429) return clientError.proxyBlock;
      if (err?.response?.status === 500) return clientError.somethingWentWrong;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.proxyBlock;
    }
  }

  //otpVerify ...
  requestRegisVerifyOTP = (otp: string) =>
    new Promise(async (resolve, reject) => {
      let body = {
        otp: otp,
      };
      try {
        await axios.post(`${REST_URI}/otp/verify`, body);
        resolve(null);
      } catch (err: any) {
        if (err?.response?.data?.message) reject(err.response.data.message);
        else reject("something went wrong");
      }
    });

  //final step of registeration
  settradeRegisteration = async (prop: settradeRegisterationInputType) => {
    try {
      if (prop.phoneNo[0] !== "0") return clientError.phoneNoMustStartWith0;

      let body = {
        phoneNo: prop.phoneNo,
        fullName: prop.fullName,
        username: prop.username,
        brokerID: prop.brokerID,
        password: prop.password,
        cfPassword: prop.cfPassword,
      };

      const res = await axios.post(`${REST_URI}/register/settrade`, body);

      if (res.status === 200) {
        const result: settradeRegisterationType = {
          __typename: "SettradeRegisteration",
          message: res.data.message,
          success: res.data.success,
        };
        return result;
      }
      return clientError.bitkubRegisFail;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  };

  bitkubRegisteration = async (prop: bitkubRegisterationInputType) => {
    try {
      if (prop.phoneNo[0] !== "0") return clientError.phoneNoMustStartWith0;

      let body = {
        brokerID: prop.brokerID,
        phoneNo: prop.phoneNo,
        fullName: prop.fullName,
        password: prop.password,
        cfPassword: prop.cfPassword,
        apiKey: prop.apiKey,
        apiSecret: prop.apiSecret,
      };

      const res = await axios.post(`${REST_URI}/register/bitkub`, body);
      if (res.status === 200) {
        const result: bitkubRegisterationType = {
          __typename: "BitkubRegisteration",
          message: res.data.message,
          success: res.data.success,
        };
        return result;
      }
      return clientError.bitkubRegisFail;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (err?.response?.status === 429) return clientError.proxyBlock;
      if (err?.response?.status === 500) return clientError.somethingWentWrong;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.proxyBlock;
    }
  };

  changePassword = (
    oldPassword: string,
    newPassword: string,
    cfNewPassword: string
  ) =>
    new Promise(async (resolve, reject) => {
      try {
        await axios.post(`${REST_URI}/changePassword`, {
          oldPassword: oldPassword,
          newPassword: newPassword,
          cfNewPassword: cfNewPassword,
        });
        resolve(null);
      } catch (err: any) {
        if (err?.response?.data?.message) {
          reject(err.response.data.message);
        } else {
          reject("something went wrong");
        }
      }
    });

  //login ...
  async login(phoneNo: string, brokerID: string, password: string) {
    if (phoneNo[0] !== "0") return clientError.phoneNoMustStartWith0;
    try {
      const res = await axios.post(`${REST_URI}/login`, {
        phoneNo: phoneNo,
        brokerID: brokerID,
        password: password,
      });
      if (res.status !== 200) return clientError.loginFail;
      return {
        __typename: "Login",
        message: res.data.message,
        success: res.data.success,
      } as loginType;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (err?.response?.status === 429) return clientError.proxyBlock;
      if (err?.response?.status === 500) return clientError.somethingWentWrong;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.proxyBlock;
    }
  }

  changePhoneNoRequest = (body?: any) =>
    new Promise(async (resolve, reject) => {
      try {
        await axios.post(`${REST_URI}/changePhoneNo/request`, body);
        resolve(null);
      } catch (err: any) {
        if (err?.response?.data?.message) reject(err.response.data);
        else reject("something went wrong");
      }
    });

  changePhoneNumber = (brokerID: string, newPhoneNo: string) =>
    new Promise(async (resolve, reject) => {
      let body = {
        brokerID: brokerID,
        newPhoneNo: newPhoneNo,
      };
      try {
        let res = await axios.post(`${REST_URI}/changePhoneNo`, body);
        resolve(res);
      } catch (err) {
        reject(err);
      }
    });

  verifyOTP = (otp: string) =>
    new Promise(async (resolve, reject) => {
      try {
        await axios.post(`${REST_URI}/otp/verify`, { otp: otp });

        resolve(null);
      } catch (err: any) {
        if (err?.response?.data?.message) reject(err.response.data.message);
        else reject("something went wrong");
      }
    });

  registerWithPhoneNumber = (
    regisByPhoneNumberGQL: any,
    regisProps: regisWithPhoneNumberProps
  ) =>
    new Promise<signinInterface>(async (resolve, reject) => {
      try {
        const res: regis_phone_response = await regisByPhoneNumberGQL({
          variables: regisProps,
        });
        if (res.data.registerWithPhoneNumber.token) {
          let data = res.data.registerWithPhoneNumber;
          this.setSession(data.token);
          var decodedToken: string = jwtDecode(data.token);
          resolve({
            user: data.user,
            decodedToken: decodedToken,
            token: data.token,
          });
        }
      } catch (err: any) {
        if (err?.response?.data?.message) reject(err.response.data.message);
        else reject("something went wrong");
      }
    });

  handleAuthentication() {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return;
    }
    if (this.isValidToken(accessToken)) {
      this.setSession(accessToken);
    } else {
      this.setSession(null);
    }
  }

  setSession = (accessToken: any) => {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    } else {
      localStorage.removeItem("accessToken");
    }
  };

  getAccessToken = () => localStorage.getItem("accessToken");

  isValidToken = (accessToken: any) => {
    if (!accessToken) {
      return false;
    }
    var decoded: any;
    try {
      decoded = jwtDecode(accessToken);
    } catch (err) {
      decoded = {
        exp: accessToken,
      };
    }
    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  };

  loginWithFB = (result: any) => {
    localStorage.setItem("accessToken", result.data_access_expiration_time);
  };

  sttAuthen = async (code: string, state: string) => {
    let body = {
      code: code,
      state: state,
    };
    try {
      const res = await axios.post(`${REST_URI}/stt/authen`, body);

      if (res.status === 200) {
        const result: sttAuthenResponseType = {
          __typename: "SttAuthen",
          accessToken: res.data.accessToken,
          brokerID: res.data.brokerID,
          code: res.data.code,
          requireRegister: res.data.requireRegister,
          brokerUsername: res.data.brokerUsername,
        };
        return result;
      }
      return clientError.somethingWentWrong;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  };

  sttRegisRequest = async (body?: any) => {
    try {
      const res = await axios.post(`${REST_URI}/stt/register/request`, body);

      if (res.status === 200) {
        const result: sttRegisRequestResponseType = {
          __typename: "SttRegisRequest",
        };
        return result;
      }
      return clientError.somethingWentWrong;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  };

  sttRegis = async (body?: sttRegisInputType) => {
    try {
      const res = await axios.post(`${REST_URI}/stt/register`, body);

      if (res.status === 200) {
        const result: sttRegisResponseType = {
          __typename: "SttRegis",
          success: res.data.success,
          message: res.data.message,
        };
        return result;
      }
      return clientError.somethingWentWrong;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  };

  sttURI = async () => {
    try {
      const res = await axios.get(`${REST_URI}/stt/request/uri`);

      if (res.status === 200) {
        const result: sttUriType = {
          __typename: "SttURI",
          callbackUri: res.data.callbackUri,
        };
        return result;
      }
      return clientError.somethingWentWrong;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  };

  changeNameURI = async () => {
    try {
      const res = await axios.get(`${REST_URI}/change/streaming/request`);

      if (res.status === 200) {
        const result: changeNameUriType = {
          __typename: "changeNameURI",
          callbackUri: res.data.callbackUri,
        };
        return result;
      }
      return clientError.somethingWentWrong;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  };

  addOpenAccount = async (phoneNo: string, brokerID: string) => {
    try {
      if (phoneNo[0] !== "0") return clientError.phoneNoMustStartWith0;

      let body = {
        phoneNo: phoneNo,
        brokerID: brokerID,
      };
      const res = await axios.post(`${REST_URI}/openAccount/add`, body);

      if (res.status === 200) {
        const result: addOpenAccountType = {
          __typename: "AddOpenAccount",
          message: res.data.message,
          success: res.data.success,
        };
        return result;
      }
      return clientError.somethingWentWrong;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  };

  async switchBroker(fromID: string, toID: string) {
    try {
      let body = {
        oldBrokerID: fromID,
        newBrokerID: toID,
      };
      const res = await axios.post(`${REST_URI}/broker/change`, body);
      if (res.status === 200) {
        const result: switchBrokerType = {
          __typename: "SwitchBroker",
          message: res.data.message,
          success: res.data.success,
        };
        return result;
      }
      return clientError.swtichBrokerFail;
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData) return makeError(errData.code, errData.message);
      return clientError.somethingWentWrong;
    }
  }

  isAuthenticated = () => !!this.getAccessToken();
}
const authService = new AuthService();

export default authService;
